/**
 * Interface representing decoded JWT payload
 */
export interface DecodedJwtToken {
  exp: number; // Expiration time (Unix timestamp in seconds)
  iat?: number; // Issued at (Unix timestamp in seconds)
  sub?: string; // Subject (user ID)
  [key: string]: unknown; // Allow other custom claims
}

/**
 * Decode JWT token and extract payload
 * Note: This does NOT verify the token signature - use only for reading claims
 *
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeJwtToken(token: string): DecodedJwtToken | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');

    if (parts.length !== 3) {
      console.warn('Invalid JWT format: expected 3 parts');
      return null;
    }

    const payload = parts[1];

    if (payload == null || payload === '') {
      console.warn('Invalid JWT: empty payload');
      return null;
    }

    // Decode base64url to JSON
    const decoded = base64UrlDecode(payload);
    const parsed = JSON.parse(decoded) as DecodedJwtToken;

    // Validate that exp exists and is a number
    if (typeof parsed.exp !== 'number') {
      console.warn('Invalid JWT: missing or invalid exp claim');
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Get token expiration timestamp in milliseconds
 *
 * @param token - JWT token string
 * @returns Expiration timestamp in milliseconds or null if invalid
 */
export function getTokenExpirationMs(token: string): number | null {
  const decoded = decodeJwtToken(token);
  if (decoded === null) {
    return null;
  }

  // Convert Unix timestamp (seconds) to milliseconds
  return decoded.exp * 1000;
}

/**
 * Check if token is expired
 *
 * @param token - JWT token string
 * @returns True if token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  const expirationMs = getTokenExpirationMs(token);
  if (expirationMs === null) {
    return true; // Treat invalid tokens as expired
  }

  return Date.now() >= expirationMs;
}

/**
 * Decode base64url string to UTF-8 string
 * Base64url is URL-safe variant of base64 used in JWT
 *
 * @param base64Url - Base64url encoded string
 * @returns Decoded UTF-8 string
 */
function base64UrlDecode(base64Url: string): string {
  // Replace URL-safe characters with standard base64 characters
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed (base64 requires length to be multiple of 4)
  const padding = base64.length % 4;
  if (padding > 0) {
    base64 += '='.repeat(4 - padding);
  }

  // Decode base64 to binary string
  const binaryString = atob(base64);

  // Convert binary string to UTF-8
  // Handle Unicode characters properly
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new TextDecoder().decode(bytes);
}
